import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { AdminHeader } from "../../components/admin/AdminLayout";
import { api, brl, downloadFile, formatError, monthLabel } from "../../lib/api";

const METHOD = { pix: "Pix", card: "Cartão", dinheiro: "Dinheiro" };
const NOW_Y = new Date().getFullYear();
const YEARS = [NOW_Y, NOW_Y - 1, NOW_Y - 2];

export default function Reports() {
  const [year, setYear] = useState(String(NOW_Y));
  const [r, setR] = useState(null);

  useEffect(() => {
    api
      .get("/admin/reports/financial", { params: { year } })
      .then((res) => setR(res.data))
      .catch((e) => toast.error(formatError(e)));
  }, [year]);

  const chart = r?.months.map((m) => ({ name: monthLabel(m.month).split(" ")[0], valor: m.received })) || [];

  return (
    <div data-testid="admin-reports-page">
      <AdminHeader title="Relatórios financeiros" subtitle="Receitas de mensalidades por mês">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28 bg-white" data-testid="report-year"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" onClick={() => downloadFile("/admin/export/payments.csv", `pagamentos-${year}.csv`, { year })} data-testid="export-year-csv">
          <Download className="w-4 h-4 mr-2" /> Pagamentos {year} (CSV)
        </Button>
        <Button variant="outline" onClick={() => downloadFile("/admin/export/members.csv", "associados.csv")} data-testid="export-members-csv-report">
          <Download className="w-4 h-4 mr-2" /> Associados (CSV)
        </Button>
      </AdminHeader>
      {r && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="rounded-2xl bg-[#052E16] text-white p-5">
              <p className="text-xs tracking-wider text-emerald-200">TOTAL RECEBIDO {r.year}</p>
              <p className="font-display text-3xl font-extrabold mt-1" data-testid="report-total">{brl(r.total_received)}</p>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-5">
              <p className="text-xs tracking-wider text-neutral-500">MENSALIDADES PAGAS</p>
              <p className="font-display text-3xl font-extrabold mt-1">{r.total_payments}</p>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-5">
              <p className="text-xs tracking-wider text-neutral-500">POR MÉTODO</p>
              <div className="mt-2 space-y-1 text-sm">
                {r.by_method.length === 0 && <p className="text-neutral-500">—</p>}
                {r.by_method.map((m) => (
                  <p key={m.method} className="flex justify-between"><span>{METHOD[m.method] || m.method}</span><strong>{brl(m.total)}</strong></p>
                ))}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white p-5 mb-6 h-72" data-testid="report-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5EDE7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => brl(v)} />
                <Bar dataKey="valor" fill="#166534" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-table">
              <thead className="text-left bg-neutral-50 text-neutral-600">
                <tr><th className="px-4 py-3">Mês</th><th className="px-4 py-3">Pagamentos</th><th className="px-4 py-3">Recebido</th></tr>
              </thead>
              <tbody>
                {r.months.map((m) => (
                  <tr key={m.month} className="border-t border-neutral-100">
                    <td className="px-4 py-2.5 font-medium capitalize">{monthLabel(m.month)}</td>
                    <td className="px-4 py-2.5">{m.paid_count}</td>
                    <td className="px-4 py-2.5 font-semibold">{brl(m.received)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
